import { Option } from '@code/innovance-react-ui-kit';
import React, { createContext, FC, useContext, useReducer } from 'react';

import { TableCardObservationRow } from 'src/front/pages/applications/card-observation/CardObservationSearch';

export enum ACTIONS {
  SEARCH_CUSTOMER,
  CLEAR,
}

type Action = { type: ACTIONS; payload?: any };

interface CardObservationState {
  beginDate: Date | null;
  endDate: Date | null;
  pos: Option | null;
  page: number;
  customerSearchValues?: TableCardObservationRow;
}

type CardObservationDispatch = (action: Action) => void;

const CardObservationStateContext = createContext<CardObservationState | undefined>(undefined);
const CardObservationDispatchContext = createContext<CardObservationDispatch | undefined>(
  undefined
);

const initialValues = {
  beginDate: null,
  endDate: null,
  pos: null,
  page: 1,
};

const cardObservationReducer = (
  state: CardObservationState,
  action: Action
): CardObservationState => {
  switch (action.type) {
    case ACTIONS.SEARCH_CUSTOMER:
      return {
        ...state,
        ...action.payload,
      };
    case ACTIONS.CLEAR:
      return initialValues;
    default:
      return state;
  }
};

const CardObservationProvider: FC = ({ children }) => {
  const [state, dispatch] = useReducer(cardObservationReducer, initialValues);

  return (
    <CardObservationStateContext.Provider value={state}>
      <CardObservationDispatchContext.Provider value={dispatch}>
        {children}
      </CardObservationDispatchContext.Provider>
    </CardObservationStateContext.Provider>
  );
};

export const useCustomerSearchState = (): CardObservationState => {
  const context = useContext(CardObservationStateContext);
  if (context === undefined) {
    throw new Error('useCustomerSearchState must be used within a CardObservationProvider');
  }

  return context;
};

type SearchInput = Pick<
  CardObservationState,
  'beginDate' | 'endDate' | 'pos' | 'customerSearchValues' | 'page'
>;

export const useCustomerSearchDispatch = (): ((payload: SearchInput) => void) => {
  const dispatch = useContext(CardObservationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useCustomerSearchDispatch must be used within a CardObservationProvider');
  }

  return (payload: SearchInput) => dispatch({ type: ACTIONS.SEARCH_CUSTOMER, payload });
};

export const useClearDispatch = (): (() => void) => {
  const dispatch = useContext(CardObservationDispatchContext);
  if (dispatch === undefined) {
    throw new Error('useClearDispatch must be used within a CardObservationProvider');
  }

  return () => dispatch({ type: ACTIONS.CLEAR });
};

export default CardObservationProvider;
